import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Users, Trash2, MoreVertical, Search, Bell, BellOff, Info, X } from 'lucide-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import { setChatChannelMute } from '../../../utils/api';
import UserStatusIndicator from '../UserStatusIndicator';
import styles from '../Chat.module.css';

const AVATAR_COLORS = [
  'var(--lodge-orange-400)', '#7C5CFC', '#12B76A', '#0BA5EC',
  '#F04438', '#F79009', '#6172F3', '#EE46BC',
];

const getAvatarColor = (id) => {
  const hash = (id || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const CustomChannelHeader = ({ onBack, isMobile, channel, onDeleteChannel }) => {
  const [showChannelMenu, setShowChannelMenu] = useState(false);
  const [isDeletingChannel, setIsDeletingChannel] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const menuRef = useRef(null);
  const currentUser = window.streamChatClient?.user;
  const { getUserOnlineStatus } = useStreamChat();

  const getChannelName = () => {
    const members = Object.values(channel?.state?.members || {});
    const otherMembers = members.filter(member => member.user?.id !== currentUser?.id);

    if (otherMembers.length === 1) {
      return otherMembers[0].user?.name || otherMembers[0].user?.id;
    } else if (otherMembers.length > 1) {
      const first = (otherMembers[0].user?.name || otherMembers[0].user?.id).split(' ')[0];
      if (otherMembers.length === 2) {
        const second = (otherMembers[1].user?.name || otherMembers[1].user?.id).split(' ')[0];
        return `${first}, ${second}`;
      }
      return `${first} + ${otherMembers.length - 1} others`;
    }

    return channel?.data?.name || 'Unknown';
  };

  const getMemberCount = () => Object.keys(channel?.state?.members || {}).length;
  const isChannelOwner = () => channel?.data?.created_by?.id === currentUser?.id;

  const handleDeleteChannel = async () => {
    if (!channel || isDeletingChannel) return;
    const confirmed = window.confirm('Are you sure you want to delete this channel? This action cannot be undone.');
    if (!confirmed) return;

    setIsDeletingChannel(true);
    try {
      await channel.delete();
      setShowChannelMenu(false);
      if (onDeleteChannel) onDeleteChannel();
    } catch (error) {
      console.error('[CustomChannelHeader] Error deleting channel:', error);
      alert('Failed to delete channel.');
    } finally {
      setIsDeletingChannel(false);
    }
  };

  const handleToggleMute = async () => {
    if (!channel) return;
    const nextMuted = !isMuted;
    const syncClientMutedChannels = (response) => {
      const client = channel.getClient();
      if (response?.channel_mutes) {
        client.mutedChannels = response.channel_mutes;
        return;
      }
      if (response?.own_user?.channel_mutes) {
        client.mutedChannels = response.own_user.channel_mutes;
        return;
      }

      const existing = Array.isArray(client.mutedChannels) ? client.mutedChannels : [];
      if (nextMuted && response?.channel_mute) {
        client.mutedChannels = [
          ...existing.filter((mute) => mute.channel?.cid !== channel.cid),
          response.channel_mute,
        ];
      } else if (!nextMuted) {
        client.mutedChannels = existing.filter((mute) => mute.channel?.cid !== channel.cid);
      }
    };

    try {
      if (nextMuted) {
        const response = await channel.mute();
        syncClientMutedChannels(response);
      } else {
        const response = await channel.unmute();
        syncClientMutedChannels(response);
      }
      setIsMuted(nextMuted);
      setChatChannelMute({
        cid: channel.cid,
        channelType: channel.type,
        channelId: channel.id,
        muted: nextMuted,
      }).catch((error) => {
        console.error('[CustomChannelHeader] Error saving mute preference:', error);
      });
    } catch (error) {
      console.error('[CustomChannelHeader] Error toggling mute:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const response = await channel.search(searchQuery, { limit: 20 });
      setSearchResults(response.results || []);
    } catch (error) {
      console.error('[CustomChannelHeader] Error searching messages:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.trim()) {
      setTimeout(() => { if (query === searchQuery) handleSearch(); }, 500);
    } else {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowChannelMenu(false);
    };
    if (showChannelMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showChannelMenu]);

  useEffect(() => {
    if (!channel) return;
    const syncMuteStatus = () => setIsMuted(channel.muteStatus?.().muted || false);
    syncMuteStatus();

    const client = channel.getClient?.();
    client?.on?.('notification.channel_mutes_updated', syncMuteStatus);
    return () => client?.off?.('notification.channel_mutes_updated', syncMuteStatus);
  }, [channel]);

  const channelName = getChannelName();
  const memberCount = getMemberCount();
  const isGroupChat = memberCount > 2;

  const getOtherUser = () => {
    const members = Object.values(channel?.state?.members || {});
    const otherMembers = members.filter(member => member.user?.id !== currentUser?.id);
    return otherMembers.length === 1 ? otherMembers[0].user : null;
  };

  const otherUser = getOtherUser();
  const { online: isOtherUserOnline, lastSeen } = otherUser ? getUserOnlineStatus(otherUser.id) : { online: false };
  const directProfileImage = !isGroupChat ? otherUser?.image : null;

  const getInitials = () => {
    if (isGroupChat) return channelName.substring(0, 2).toUpperCase();
    const members = Object.values(channel?.state?.members || {});
    const otherMember = members.find(member => member.user?.id !== currentUser?.id);
    if (otherMember?.user) {
      const userName = otherMember.user.name || otherMember.user.id;
      const names = userName.split(' ');
      if (names.length > 1) return `${names[0][0]}${names[1][0]}`.toUpperCase();
      return userName.substring(0, 2).toUpperCase();
    }
    const names = channelName.split(' ');
    if (names.length > 1) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return channelName.substring(0, 2).toUpperCase();
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ background: 'var(--lodge-white)', borderBottom: '1px solid var(--lodge-onyx-100)' }}>
      <div className={styles.chatHeader}>
        <div className={styles.chatHeaderInfo}>
          {isMobile && (
            <button onClick={onBack} className={styles.headerAction} style={{ marginRight: 'var(--sp-2)' }}>
              <ArrowLeft size={20} />
            </button>
          )}

          {/* Avatar */}
          <div style={{ position: 'relative', marginRight: 'var(--sp-3)', flexShrink: 0 }}>
            <div className={styles.avatar} style={{ background: getAvatarColor(channel?.id) }}>
              {directProfileImage ? (
                <img
                  src={directProfileImage}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                />
              ) : isGroupChat ? (
                <Users size={18} />
              ) : (
                getInitials()
              )}
            </div>
            {otherUser && !isGroupChat && (
              <div style={{ position: 'absolute', bottom: -2, right: -2 }}>
                <UserStatusIndicator userId={otherUser.id} size="sm" showBorder={true} />
              </div>
            )}
          </div>

          {/* Channel info */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 className={styles.chatHeaderName} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }} title={channelName}>
              {channelName}
              {isMuted && <BellOff size={14} style={{ color: 'var(--lodge-onyx-300)' }} />}
            </h2>
            <div className={styles.chatHeaderMeta}>
              {isGroupChat ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Users size={12} />
                  {memberCount} members
                </span>
              ) : otherUser ? (
                isOtherUserOnline ? (
                  <span style={{ color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span className={styles.onlineDot} />
                    Online now
                  </span>
                ) : (
                  <span>Last seen {formatLastSeen(lastSeen)}</span>
                )
              ) : null}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <button
            onClick={() => setShowSearch(!showSearch)}
            className={`${styles.headerAction} ${showSearch ? styles.active : ''}`}
            title="Search in conversation"
          >
            <Search size={18} />
          </button>

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowChannelMenu(!showChannelMenu)}
              className={styles.headerAction}
              title="More options"
            >
              <MoreVertical size={18} />
            </button>

            {showChannelMenu && (
              <div ref={menuRef} className={styles.dropdown}>
                <button onClick={() => setShowChannelMenu(false)} className={styles.dropdownItem}>
                  <Info size={16} style={{ color: 'var(--lodge-onyx-300)' }} />
                  Channel Info
                </button>

                {isGroupChat && (
                  <button onClick={() => setShowChannelMenu(false)} className={styles.dropdownItem}>
                    <Users size={16} style={{ color: 'var(--lodge-onyx-300)' }} />
                    View Members
                  </button>
                )}

                <button
                  onClick={() => { handleToggleMute(); setShowChannelMenu(false); }}
                  className={styles.dropdownItem}
                >
                  {isMuted ? (
                    <><Bell size={16} style={{ color: 'var(--lodge-onyx-300)' }} /> Unmute Notifications</>
                  ) : (
                    <><BellOff size={16} style={{ color: 'var(--lodge-onyx-300)' }} /> Mute Notifications</>
                  )}
                </button>

                {isChannelOwner() && (
                  <>
                    <div className={styles.dropdownDivider} />
                    <button
                      onClick={handleDeleteChannel}
                      disabled={isDeletingChannel}
                      className={`${styles.dropdownItem} ${styles.danger}`}
                      style={{ opacity: isDeletingChannel ? 0.5 : 1 }}
                    >
                      {isDeletingChannel ? (
                        <><div className={styles.spinner} style={{ width: 16, height: 16, borderWidth: 2, borderTopColor: 'var(--color-error)' }} /> Deleting...</>
                      ) : (
                        <><Trash2 size={16} /> Delete Channel</>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search Interface */}
      {showSearch && (
        <div className={styles.headerSearchWrap}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div style={{ position: 'absolute', left: 'var(--sp-3)', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <Search size={16} style={{ color: 'var(--lodge-onyx-300)' }} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder="Search"
              className={styles.headerSearchInput}
              autoFocus
            />
            {isSearching && (
              <div style={{ position: 'absolute', right: 'var(--sp-3)', top: '50%', transform: 'translateY(-50%)' }}>
                <div className={styles.spinner} style={{ width: 16, height: 16, borderWidth: 2 }} />
              </div>
            )}
            {!isSearching && searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                style={{ position: 'absolute', right: 'var(--sp-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--lodge-onyx-300)' }}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}
            className={styles.headerSearchCancel}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomChannelHeader;
